import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  LogOut,
  BellIcon,
  Mail,
  Loader2,
  Menu,
  ChevronDown,
  ArrowRight,
  Settings as SettingsIcon,
  Copy,
  Check,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createProfileShare,
  getProfileShare,
  type ProfileShareSummary,
} from "@/api/service/profile";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/hooks/useAuth";
import type { User } from "@/types/auth";
import SignInModal from "@/components/SignInModal";
import ContactModal from "@/components/ContactModal";
import { ThemeToggle } from "@/components/ThemeToggle";
import NotificationPanel from "@/components/NotificationPanel";
import NotificationDetailModal from "@/components/NotificationDetailModal";
import {
  getNotifications,
  markAllNotificationsAsRead,
  markAnnouncementAsRead,
  notificationKey,
} from "@/api/service/notifications";
import { Notification } from "@/api/service/notifications";
import { BranchPill } from "./BranchPill";
import { usePass, usePassPricing } from "@/hooks/usePass";
import { NAV_ITEMS, isNavItemActive, type NavItem } from "./nav-items";
import { cn } from "@/lib/utils";

export default function NavBar() {
  const { user, signOut, isLoading } = useAuth();

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-card">
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6">
        {/* Row 1 — identity and account */}
        <div className="flex h-14 items-center gap-4 lg:h-[58px] lg:gap-6">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <BrandMark size={26} />
            <span className="text-base font-semibold tracking-[-0.01em]">
              gatetard
            </span>
          </Link>

          {/* Desktop links sit inline; below lg they move to row 2. */}
          <div className="hidden lg:block">
            <NavLinks />
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {user && (
              <>
                {/* The full branch name needs room; below sm the code alone
still identifies it. */}
                <div className="hidden sm:block">
                  <BranchPill />
                </div>
                <div className="sm:hidden">
                  <BranchPill compact />
                </div>
              </>
            )}
            {/* Signed out, the phone bar is the brand, the ask and the menu
                — there is no room for a third control beside them, and the
                theme still follows the OS via next-themes' `system` default.
                Desktop has the room, so it keeps the toggle. Signed in it is
                unconditional, as before. */}
            {user ? (
              <ThemeToggle />
            ) : (
              <div className="hidden lg:block">
                <ThemeToggle />
              </div>
            )}
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : user ? (
              <>
                <NotificationBell />
                <ProfileDropdown user={user} signOut={signOut} />
              </>
            ) : (
              <>
                <SignInButton />
                <MobileNavDrawer />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-border/70 lg:hidden">
        <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6">
          <NavLinks scrollable />
        </div>
      </div>
    </nav>
  );
}

/**
 * The signed-out mobile nav — the design's hamburger.
 *
 * Links only: the same NAV_ITEMS the desktop bar shows inline, so nothing is
 * reachable from here alone. Appearance is deliberately not here — on a
 * phone the theme follows the OS, and the drawer is for going somewhere.
 */
function MobileNavDrawer() {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();

  // Navigating from inside the drawer should leave it behind.
  React.useEffect(() => setOpen(false), [pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          aria-label="Open menu"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-secondary lg:hidden"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-[280px] p-0 sm:max-w-[280px]">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <SheetDescription className="sr-only">
          Browse gatetard
        </SheetDescription>

        <nav className="flex flex-col px-3 pt-14">
          {NAV_ITEMS.map((item) => {
            const active = isNavItemActive(item, pathname);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-3 py-3 text-[15px] transition-colors",
                  active
                    ? "bg-secondary font-semibold text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {item.label}
                {item.beta && (
                  <span className="rounded-full border border-border px-1.5 py-px font-mono text-[9px] uppercase tracking-[0.08em] text-muted-foreground">
                    Beta
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

function NavLinks({ scrollable = false }: { scrollable?: boolean }) {
  const { pathname } = useLocation();
  const stripRef = useRef<HTMLDivElement>(null);

  // Keep the active tab in view on the mobile strip — landing on Progress
  // from a deep link shouldn't leave the highlighted tab off-screen.
  useEffect(() => {
    if (!scrollable || !stripRef.current) return;
    const active = stripRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [pathname, scrollable]);

  return (
    <div
      ref={stripRef}
      className={cn(
        "flex items-center gap-5 text-sm sm:gap-6",
        scrollable && "scroll-x -mx-4 px-4 sm:-mx-6 sm:px-6",
      )}
    >
      {NAV_ITEMS.map((item) => (
        <NavItemLink
          key={item.to}
          item={item}
          active={isNavItemActive(item, pathname)}
          compactHeight={scrollable}
        />
      ))}
    </div>
  );
}

function NavItemLink({
  item,
  active,
  compactHeight,
}: {
  item: NavItem;
  active: boolean;
  compactHeight: boolean;
}) {
  return (
    <NavLink
      to={item.to}
      data-active={active}
      className={cn(
        "flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 transition-colors",
        compactHeight ? "h-11" : "h-[57px]",
        active
          ? "border-foreground font-semibold text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {item.label}
      {item.beta && (
        <span className="rounded-full border border-border px-1.5 py-px font-mono text-[9px] uppercase tracking-[0.08em] text-muted-foreground">
          Beta
        </span>
      )}
    </NavLink>
  );
}

const notificationsKey = ["notifications"] as const;

const NotificationBell = () => {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [bellOpen, setBellOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [active, setActive] = useState<Notification | null>(null);

  // One shared query, not per-mount local state: the bell remounts with the
  // layout, and each remount used to be another GET /notifications.
  const query = useQuery({
    queryKey: notificationsKey,
    queryFn: getNotifications,
    staleTime: 60 * 1000,
  });
  const notifications: Notification[] = query.data?.data ?? [];
  const loading = query.isLoading;
  const setNotifications = (
    next: Notification[] | ((prev: Notification[]) => Notification[]),
  ) =>
    qc.setQueryData(notificationsKey, (prev: typeof query.data) => ({
      ...(prev ?? { success: true }),
      data: typeof next === "function" ? next(prev?.data ?? []) : next,
    }));

  const clearAll = async () => {
    if (notifications.length === 0) return;
    try {
      setClearing(true);
      const res = await markAllNotificationsAsRead();
      if (res.success) setNotifications([]);
      else setError(res.message || "Failed to clear notifications");
    } catch (err) {
      console.error(err);
      setError("Failed to clear notifications");
    } finally {
      setClearing(false);
    }
  };

  // Open the detail modal. For announcements, mark them as read in the
  // background and optimistically drop them from the feed — the user just
  // saw the content, so the unread state is no longer interesting.
  const openDetail = (n: Notification) => {
    setBellOpen(false);
    setActive(n);
    if (n.kind === "announcement" && n.announcement_id) {
      const id = n.announcement_id;
      markAnnouncementAsRead(id).catch((err) => {
        console.error("Failed to mark announcement read", err);
      });
      setNotifications((prev) =>
        prev.filter((x) => notificationKey(x) !== notificationKey(n)),
      );
    }
  };

  return (
    <>
      <DropdownMenu open={bellOpen} onOpenChange={setBellOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={
              notifications.length > 0
                ? `Notifications, ${notifications.length} unread`
                : "Notifications"
            }
            className="relative h-8 w-8 rounded-full border border-border"
          >
            <BellIcon className="h-4 w-4 text-muted-foreground" />
            {notifications.length > 0 && (
              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-teal" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="bottom"
          className="w-auto rounded-xl p-0"
        >
          <NotificationPanel
            notifications={notifications}
            loading={loading}
            error={error ?? (query.isError ? "Failed to load notifications" : null)}
            clearing={clearing}
            clearAll={clearAll}
            onOpenDetail={openDetail}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <NotificationDetailModal
        notification={active}
        open={!!active}
        onClose={() => setActive(null)}
      />
    </>
  );
};

/**
 * The one ask in the nav bar.
 *
 * Squared off and full weight rather than the old small pill: signed out
 * this is the only action in the bar, and the design sizes it accordingly.
 * The arrow is desktop-only — on a 390px bar it competes with the hamburger
 * sitting right beside it, and the design drops it there too.
 */
const SignInButton = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="group flex h-9 items-center gap-2 rounded-lg bg-primary px-3.5 text-[13.5px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 sm:px-4 sm:text-[14px]"
      >
        Sign in
        <ArrowRight className="hidden h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 lg:block" />
      </button>
      <SignInModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

/**
 * Account menu — account items only.
 *
 * Everything that used to live here (Progress, Practice, Contests, My
 * Tests, Bookmarks) is now in the nav bar. The design is explicit that this
 * menu must not become a hiding place for features again: if you are about
 * to add a study feature here, it belongs in nav-items.ts instead.
 */
const ProfileDropdown = ({
  user,
  signOut,
}: {
  user: User;
  signOut: () => void;
}) => {
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  };

  const initial = (user.name || user.email || "?")
    .trim()
    .charAt(0)
    .toUpperCase();
  const pass = usePass();
  const { price } = usePassPricing();

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className={cn(
              "flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2 transition-colors hover:bg-secondary",
              // A pass holder's avatar carries a hairline teal ring. That is
              // the whole indicator — no badge text in the bar.
              pass.active ? "border-teal/60" : "border-border",
            )}
          >
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                referrerPolicy="no-referrer"
                className="h-6 w-6 rounded-full"
              />
            ) : (
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground">
                {initial}
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-3 w-3 text-muted-foreground transition-transform",
                isDropdownOpen && "rotate-180",
              )}
            />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-56 rounded-xl p-1"
          align="end"
          forceMount
        >
          <div className="border-b border-border px-3 py-2.5">
            <p className="truncate text-sm font-medium">{user.name}</p>
            {user.email && (
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {user.email}
              </p>
            )}
            {pass.active ? (
              <span className="pass-sheen mt-2 inline-flex items-center rounded-md px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em]">
                GATE 2027 Pass
              </span>
            ) : (
              <Link
                to="/pricing"
                onClick={() => setIsDropdownOpen(false)}
                className="pass-sheen mt-2 flex items-center justify-between rounded-lg px-2.5 py-2 text-[12.5px] font-semibold transition-transform hover:scale-[1.01]"
              >
                <span>Unlock everything</span>
                <span className="font-mono text-[11px] opacity-80">₹{price}</span>
              </Link>
            )}
          </div>

          <DiscordCodeSection open={isDropdownOpen} />

          <div className="py-1">
            <DropdownMenuItem
              className="mx-0.5 flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm"
              onClick={() => navigate("/settings")}
            >
              <SettingsIcon className="h-3.5 w-3.5 text-muted-foreground" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              className="mx-0.5 flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm"
              onClick={() => setIsContactModalOpen(true)}
            >
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              Support
            </DropdownMenuItem>
          </div>

          <DropdownMenuSeparator />

          <div className="py-1">
            <DropdownMenuItem
              className="mx-0.5 flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-destructive focus:text-destructive"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <LogOut className="h-3.5 w-3.5" />
              )}
              {isSigningOut ? "Signing out..." : "Sign out"}
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </>
  );
};

const SHARE_SUMMARY_KEY = ["profile", "share-summary"];

/**
 * The user's code for linking Discord — shown in the account menu because
 * "where do I get the code?" is the first question every /link user asks.
 * Reads are light (GET, no card render); the code is only minted on demand.
 */
const DiscordCodeSection = ({ open }: { open: boolean }) => {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: SHARE_SUMMARY_KEY,
    queryFn: getProfileShare,
    enabled: open,
    staleTime: Infinity,
  });

  const mint = useMutation({
    mutationFn: createProfileShare,
    onSuccess: (res) => {
      if (res.success && res.data) {
        const summary: ProfileShareSummary = {
          share_code: res.data.share_code,
          share_url: res.data.share_url,
          enabled: res.data.enabled,
        };
        queryClient.setQueryData(SHARE_SUMMARY_KEY, {
          ...res,
          data: summary,
        });
      }
    },
  });

  const share = data?.data ?? null;

  const copy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.share_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard unavailable — leave the code visible for manual copy.
    }
  };

  return (
    <div className="border-b border-border px-3 py-2.5">
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Profile ID
      </p>

      {isLoading ? (
        <div className="mt-1.5 h-8 animate-pulse rounded-lg bg-secondary" />
      ) : share ? (
        <>
          <button
            type="button"
            onClick={copy}
            aria-label={copied ? "Copied" : "Copy profile ID"}
            className="mt-1.5 flex w-full items-center justify-between gap-2 rounded-lg border border-dashed border-border bg-secondary/50 px-2.5 py-1.5 font-mono text-[13px] tracking-[0.06em] transition-colors hover:bg-secondary"
          >
            {share.share_code}
            {copied ? (
              <Check className="h-3.5 w-3.5 text-teal" />
            ) : (
              <Copy className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
          {!share.enabled && (
            <p className="mt-1 text-[10.5px] leading-snug text-muted-foreground">
              Sharing is off — enable it in Progress → Share
            </p>
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={() => mint.mutate()}
          disabled={mint.isPending}
          className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/50 px-2.5 py-1.5 text-[13px] font-medium transition-colors hover:bg-secondary disabled:opacity-60"
        >
          {mint.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            "Generate ID"
          )}
        </button>
      )}
    </div>
  );
};
